// src/components/ErrorBanner.jsx
//
// Generic error banner. Surfaces the message and (when present) a
// dismiss button. Per spec, classification is fed in by the caller -
// this component just renders. Uses log.error for telemetry.
//
// severity="error"   (default) — red tones, role="alert"
// severity="warning"           — amber/yellow tones, role="status"

import React from "react";
import { T } from "../app/tokens.js";

const SEVERITY_STYLES = {
  error: {
    background: T.utilRed,
    border: `1px solid ${T.red}`,
    color: T.red,
    role: "alert",
    dismissColor: T.red,
    dismissLabel: "Dismiss error",
  },
  warning: {
    background: T.utilAmber,
    border: `1px solid ${T.amber}`,
    color: T.amberDark,
    role: "status",
    dismissColor: T.amberDark,
    dismissLabel: "Dismiss warning",
  },
};

const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

export function ErrorBanner({ error, onDismiss, severity = "error" }) {
  const msg = error ? (error?.message || String(error)) : "";
  const styles = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.error;
  return (
    <>
      {/* Persistent live region — always mounted so screen readers reliably
          catch the announcement even when the element is inserted late.
          role="alert" on a conditionally-rendered node can be missed by some
          screen reader + browser combinations; updating text inside a
          pre-existing aria-live region is universally reliable. */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        data-testid="error-live-region"
        style={srOnly}
      >
        {msg}
      </div>
      {error && (
        <div
          role={styles.role}
          style={{
            background: styles.background,
            border: styles.border,
            color: styles.color,
            padding: "10px 14px",
            borderRadius: 8,
            margin: "12px 0",
            fontSize: T.fsBase,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontWeight: T.fwBold }}>{error?.name || "Error"}:</span>
          <span style={{ flex: 1 }}>{msg}</span>
          {onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              onKeyDown={(e) => {
                // Explicitly handle Enter and Space so that fireEvent.keyDown in
                // tests (and keyboard users in real browsers) dismiss the banner
                // with a single call.  e.preventDefault() suppresses the native
                // button click that browsers fire on these keys, ensuring
                // onDismiss is invoked exactly once per keypress.
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onDismiss();
                }
              }}
              style={{
                background: "transparent",
                border: "none",
                color: styles.dismissColor,
                cursor: "pointer",
                fontSize: T.fsLg,
                fontWeight: T.fwBold,
              }}
              aria-label={styles.dismissLabel}
            >
              ×
            </button>
          )}
        </div>
      )}
    </>
  );
}
